/**
 * BibleLandingPage - Public landing page for Bible module
 * Hero + features + CTA (login/register)
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen, Calendar, Trophy, Users, Heart, Bell,
  ArrowRight, CheckCircle2, Sparkles, Star, BookMarked
} from 'lucide-react';

const BibleLandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: BookOpen,
      title: 'Full Bible Access',
      description: 'Read the complete King James Version with easy navigation between books and chapters.',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Calendar,
      title: 'Reading Plans',
      description: 'Follow structured reading plans like McCheyne, Bible in a Year, or 30 Days with Jesus.',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: Trophy,
      title: 'Bible Challenges',
      description: 'Join reading challenges, quizzes, and trivia competitions with your community.',
      color: 'bg-amber-100 text-amber-600'
    },
    {
      icon: Bell,
      title: 'Daily Reminders',
      description: 'Get email reminders for your daily readings and track your streak.',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: Heart,
      title: 'Church Devotionals',
      description: 'Subscribe to devotionals from your church and grow together in faith.',
      color: 'bg-pink-100 text-pink-600'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'See leaderboards, share progress, and encourage fellow believers.',
      color: 'bg-indigo-100 text-indigo-600'
    }
  ];

  const readingPlans = [
    { name: "McCheyne Bible Reading Plan", days: 365, description: "Read through the Bible in one year" },
    { name: "New Testament in 90 Days", days: 90, description: "Focus on the New Testament" },
    { name: "30 Days with Jesus", days: 30, description: "Walk through the Gospels" },
    { name: "Psalms & Proverbs", days: 62, description: "Wisdom and worship daily" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <BookOpen className="h-8 w-8 text-indigo-600" />
              <span className="text-xl font-bold text-slate-900">Bible App</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/bible')}>
                Read Bible
              </Button>
              <Button variant="ghost" onClick={() => navigate('/login')}>
                Sign In
              </Button>
              <Button onClick={() => navigate('/login')} data-testid="bible-get-started-btn">
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
            <Sparkles className="h-3 w-3 mr-1" /> Free Bible Reading App
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Grow Your Faith with
            <span className="text-indigo-600 block mt-2">Daily Bible Reading</span>
          </h1>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Access the complete Bible, follow reading plans, join challenges, and connect with your church community - all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/bible')} className="text-lg px-8" data-testid="start-reading-btn">
              <BookOpen className="mr-2 h-5 w-5" /> Start Reading
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/login')} className="text-lg px-8">
              View Reading Plans <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Everything You Need for Bible Study</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From daily reading to community challenges, we've got you covered.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-lg ${feature.color} flex items-center justify-center mb-4`}>
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reading Plans Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Popular Reading Plans</h2>
            <p className="text-slate-600">Choose a plan that fits your schedule and goals.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {readingPlans.map((plan, i) => (
              <Card key={i} className="hover:border-indigo-300 transition-colors cursor-pointer" onClick={() => navigate('/login')}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <BookMarked className="h-5 w-5 text-indigo-600" />
                    <Badge variant="secondary">{plan.days} days</Badge>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{plan.name}</h3>
                  <p className="text-sm text-slate-500">{plan.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-indigo-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Start Your Bible Journey Today
          </h2>
          <p className="text-indigo-100 mb-8 text-lg">
            Join thousands of believers growing in their faith through daily Bible reading.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" onClick={() => navigate('/bible')} className="text-lg px-8">
              <BookOpen className="mr-2 h-5 w-5" /> Read Now - It's Free
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 bg-transparent text-white border-white hover:bg-white/10" onClick={() => navigate('/login')}>
              Create Account
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-indigo-600" />
            <span className="font-semibold text-slate-900">Bible App</span>
          </div>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BibleLandingPage;
